import React from 'react';

/**
 * Computes the difference between two objects and returns an object containing only the changed fields.
 * Works with nested objects and handles arrays.
 * 
 * @param original The original object
 * @param updated The updated object to compare against
 * @returns An object containing only the fields that have changed
 */
export function objectDiff<T extends Record<string, any>>(original: T, updated: T): Partial<T> {
    // If either value is null or undefined, check for equality
    if (original === null || updated === null || original === undefined || updated === undefined) {
        return original === updated ? {} as Partial<T> : updated as Partial<T>;
    }

    // Handle primitive types
    if (typeof original !== 'object' || typeof updated !== 'object') {
        return original === updated ? {} as Partial<T> : updated as Partial<T>;
    }

    // Handle arrays
    if (Array.isArray(original) && Array.isArray(updated)) {
        if (JSON.stringify(original) === JSON.stringify(updated)) {
            return {} as Partial<T>;
        }
        return updated as Partial<T>;
    }

    const diff = {} as Partial<T>;
    const allKeys = new Set([...Object.keys(original), ...Object.keys(updated)]);

    for (const key of allKeys) {
        const originalValue = original[key];
        const updatedValue = updated[key];

        // Case 1: Key exists in updated but not in original
        if (!(key in original)) {
            diff[key as keyof T] = updatedValue;
            continue;
        }

        // Case 2: Key exists in original but not in updated
        if (!(key in updated)) {
            diff[key as keyof T] = undefined as any;
            continue;
        }

        // Case 3: Both have the key but the values are different types
        if (typeof originalValue !== typeof updatedValue) {
            diff[key as keyof T] = updatedValue;
            continue;
        }

        // Case 4: Both have the key and they are objects
        if (typeof originalValue === 'object' && originalValue !== null &&
            typeof updatedValue === 'object' && updatedValue !== null) {

            const nestedDiff = objectDiff(originalValue, updatedValue);

            // Only add to diff if there were actually changes
            if (Object.keys(nestedDiff).length > 0) {
                diff[key as keyof T] = nestedDiff as any;
            }

            continue;
        }

        // Case 5: Primitive values that differ
        if (originalValue !== updatedValue) {
            diff[key as keyof T] = updatedValue;
        }
    }

    return diff;
}

// Usage example:
interface User {
    id: number;
    name: string;
    email: string;
    address: {
        street: string;
        city: string;
        zipCode: string;
    };
    preferences: {
        theme: string;
        notifications: boolean;
        language: string;
    };
    tags: string[];
}

// Original user object
const originalUser: User = {
    id: 1,
    name: "John Doe",
    email: "john@example.com",
    address: {
        street: "123 Main St",
        city: "New York",
        zipCode: "10001"
    },
    preferences: {
        theme: "light",
        notifications: true,
        language: "en"
    },
    tags: ["user", "premium"]
};

// Updated user object with some changes
const updatedUser: User = {
    id: 1,
    name: "John Doe",  // unchanged
    email: "johndoe@example.com", // changed
    address: {
        street: "456 Park Ave", // changed
        city: "New York", // unchanged
        zipCode: "10001" // unchanged
    },
    preferences: {
        theme: "dark", // changed
        notifications: true, // unchanged
        language: "en" // unchanged
    },
    tags: ["user", "premium", "active"] // changed
};

// Get the diff
const diff = objectDiff(originalUser, updatedUser);
console.log("Diff:", diff);
// Expected output:
// Diff: {
//   email: "johndoe@example.com",
//   address: {
//     street: "456 Park Ave"
//   },
//   preferences: {
//     theme: "dark"
//   },
//   tags: ["user", "premium", "active"]
// }

/**
 * Applies a diff object to a state variable.
 * This function is designed to work with React's useState.
 * 
 * @param original The original object state
 * @param diff The diff object containing only the changed fields
 * @returns A new object with the diff applied
 */
export function applyDiff<T extends Record<string, any>>(original: T, diff: Partial<T>): T {
    // If diff is empty, return the original
    if (!diff || Object.keys(diff).length === 0) {
        return original;
    }

    // Create a deep copy of the original object to avoid mutation
    const result = JSON.parse(JSON.stringify(original)) as T;

    // Apply each change in the diff
    for (const key in diff) {
        const diffValue = diff[key];

        // Case 1: If the diff value is undefined, delete the property
        if (diffValue === undefined) {
            delete result[key];
            continue;
        }

        // Case 2: If the diff value is not an object or original doesn't have this key as an object,
        // simply assign the new value
        if (
            typeof diffValue !== 'object' ||
            diffValue === null ||
            typeof result[key] !== 'object' ||
            result[key] === null
        ) {
            result[key] = diffValue as any;
            continue;
        }

        // Case 3: Both are objects, recursively apply the diff
        result[key] = applyDiff(result[key], diffValue as any);
    }

    return result;
}

/**
 * React hook for managing state with diff tracking and application.
 * 
 * @param initialState The initial state
 * @returns Array containing the current state, a setter function, and a function to apply diffs
 */
export function useDiffState<T extends Record<string, any>>(initialState: T): [
    T,
    React.Dispatch<React.SetStateAction<T>>,
    (diff: Partial<T>) => void
] {
    const [state, setState] = React.useState<T>(initialState);

    // Function to apply a diff to the current state
    const applyStateDiff = React.useCallback((diff: Partial<T>) => {
        setState(currentState => applyDiff(currentState, diff));
    }, []);

    return [state, setState, applyStateDiff];
}
